/**
 * Lightweight activity tracker for clients.
 * - useTrackModuleView(moduleName) — POSTs a single module_view on mount.
 * - useTrackSearch(moduleName, queryString) — debounced POST of search terms (>=2 chars).
 * Tracking is fire-and-forget; failures are swallowed silently.
 */
import { useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEBOUNCE_MS = 900;
const MIN_QUERY_LEN = 2;

function postSilently(body) {
  axios
    .post(`${API}/activity/track`, body, { withCredentials: true })
    .catch(() => {
      /* fail silently — analytics must never break UX */
    });
}

export function useTrackModuleView(moduleName) {
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current || !moduleName) return;
    sent.current = true;
    postSilently({ type: "module_view", module: moduleName });
  }, [moduleName]);
}

export function useTrackSearch(moduleName, query) {
  const lastSent = useRef("");
  const timer = useRef(null);

  useEffect(() => {
    if (!moduleName) return undefined;
    if (timer.current) clearTimeout(timer.current);
    const trimmed = (query || "").trim();
    if (trimmed.length < MIN_QUERY_LEN) return undefined;
    if (trimmed.toLowerCase() === lastSent.current) return undefined;

    timer.current = setTimeout(() => {
      lastSent.current = trimmed.toLowerCase();
      postSilently({ type: "search", module: moduleName, query: trimmed });
    }, DEBOUNCE_MS);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [moduleName, query]);
}
